import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import VideoTile from "@/components/VideoTile";
import MotionAlerts from "@/components/MotionAlerts";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { istDateStr, istTimeStr } from "@/lib/time";
import { captureFromVideo, saveSnapshot } from "@/lib/gallery";
import {
  Grid2X2,
  Grid3X3,
  Square,
  ChevronLeft,
  ChevronRight,
  Camera as CameraIcon,
  Circle,
  X,
  PanelRight,
  Camera,
} from "lucide-react";

const LAYOUTS = {
  "1": { cols: 1, count: 1, label: "1x1" },
  "4": { cols: 2, count: 4, label: "2x2" },
  "9": { cols: 3, count: 9, label: "3x3" },
  "16": { cols: 4, count: 16, label: "4x4" },
};

const GRID_CLASSES = {
  "1": "grid-cols-1",
  "4": "grid-cols-1 sm:grid-cols-2",
  "9": "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  "16": "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [cameras, setCameras] = useState([]);
  const [layoutKey, setLayoutKey] = useState("4");
  const [expandedCam, setExpandedCam] = useState(null);
  const [page, setPage] = useState(0);
  const [sideOpen, setSideOpen] = useState(false); // mobile drawer

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/cameras")
      .then((r) => mounted && setCameras(r.data))
      .catch((e) => mounted && toast.error(formatApiError(e)));
    load();
    // Kick off backend warmup so all cameras' footages are cached before user clicks them
    api.post("/cameras/warmup").catch(() => { /* non-fatal */ });
    // Poll every 20s so operator's live/recorded toggle propagates to client dashboard
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  /* Snap every currently-visible camera tile at once. Reads the actual <video>
     elements from the DOM (no ref plumbing) and pushes each frame into the
     browser gallery. Handy for incident review — one press captures the entire
     wall. Fires on the "S" key or the toolbar button. */
  const tenantId = user?.tenant_id || "default";
  const snapAll = () => {
    const tiles = document.querySelectorAll('[data-testid^="camera-tile-"]');
    let saved = 0;
    tiles.forEach((tile) => {
      const video = tile.querySelector("video");
      if (!video || !video.videoWidth) return;
      const ch = tile.getAttribute("data-testid").replace("camera-tile-", "");
      const cam = cameras.find((c) => String(c.channel) === ch);
      if (!cam) return;
      const line1 = `CH${String(cam.channel).padStart(2, "0")} · ${cam.name}`;
      const line2 = `${istDateStr()} ${istTimeStr()} IST`;
      const cap = captureFromVideo(video, [line1, line2]);
      if (!cap) return;
      saveSnapshot(tenantId, {
        id: `${cam.id}_${Date.now()}_bulk_${cam.channel}`,
        cameraId: cam.id,
        channel: cam.channel,
        cameraName: cam.name,
        location: cam.location || "",
        timestamp: new Date().toISOString(),
        istDate: istDateStr(),
        istTime: istTimeStr(),
        thumb: cap.thumbUrl,
      });
      saved += 1;
    });
    if (saved === 0) toast.error("No live frames to capture — try again in a moment");
    else toast.success(`Snapped ${saved} camera${saved !== 1 ? "s" : ""} → Gallery`);
  };

  // Hotkey: press "S" (without any modifier + not inside a form field) to snap all.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        snapAll();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cameras, tenantId]);

  const layout = LAYOUTS[layoutKey];
  const start = page * layout.count;
  const visible = cameras.slice(start, start + layout.count);
  // pad with null slots
  const slots = [...visible];
  while (slots.length < layout.count) slots.push(null);
  const totalPages = Math.max(1, Math.ceil(cameras.length / layout.count));

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex flex-col overflow-x-hidden pb-14 md:pb-0">
      <AppHeader active="dashboard" />

      {/* Toolbar */}
      <div className="border-b border-zinc-900 bg-zinc-950 px-2 sm:px-4 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0 overflow-x-auto no-scrollbar">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2 hidden sm:inline">Layout</span>
          {Object.entries(LAYOUTS).map(([k, v]) => {
            const Icon = k === "1" ? Square : k === "4" ? Grid2X2 : Grid3X3;
            return (
              <Button
                key={k}
                data-testid={`layout-${v.label}`}
                variant="ghost"
                onClick={() => { setLayoutKey(k); setPage(0); }}
                className={`rounded-none h-8 px-2 shrink-0 ${layoutKey === k ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/40" : "text-zinc-400 border border-transparent"}`}
              >
                <Icon size={14} className="sm:mr-1.5" />
                <span className="mono text-[11px] hidden sm:inline">{v.label}</span>
              </Button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 md:gap-4 shrink-0">
          <div className="mono text-[10px] text-zinc-500 hidden sm:block">
            {cameras.length} CAMERA{cameras.length !== 1 ? "S" : ""} · PAGE {page + 1}/{totalPages}
          </div>
          <Button
            data-testid="snap-all-btn"
            variant="ghost"
            onClick={snapAll}
            title="Snap every visible camera into the gallery (S)"
            className="rounded-none h-8 px-2 text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/15 mono text-[10px] uppercase tracking-widest"
          >
            <Camera size={13} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Snap All</span>
          </Button>
          <div className="flex items-center gap-1">
            <Button
              data-testid="page-prev"
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-none h-8 w-8 p-0 text-zinc-400"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              data-testid="page-next"
              variant="ghost"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="rounded-none h-8 w-8 p-0 text-zinc-400"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
          {/* Mobile-only sidebar toggle */}
          <Button
            data-testid="mobile-panel-btn"
            variant="ghost"
            onClick={() => setSideOpen(true)}
            className="md:hidden rounded-none h-8 w-8 p-0 text-zinc-300 border border-zinc-800"
            title="Panel"
          >
            <PanelRight size={15} />
          </Button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_240px] min-h-0">
        {/* Video wall */}
        <div className="p-2 sm:p-3 bg-black min-h-0 overflow-y-auto">
          {cameras.length === 0 ? (
            <div className="h-full flex items-center justify-center border border-dashed border-zinc-900 mono text-[11px] text-zinc-600 uppercase tracking-widest p-4 text-center">
              No cameras configured — ask admin to add cameras
            </div>
          ) : (
            <div
              data-testid="video-grid"
              className={`grid gap-[1px] bg-zinc-900 content-start ${GRID_CLASSES[layoutKey]}`}
            >
              {slots.map((cam, i) =>
                cam ? (
                  <VideoTile key={cam.id} camera={cam} onExpand={() => setExpandedCam(cam)} />
                ) : (
                  <div key={`empty-${i}`} className="bg-black border border-zinc-900 aspect-video flex items-center justify-center">
                    <div className="mono text-[10px] text-zinc-800 uppercase tracking-widest">No Camera</div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Right panel: PTZ + camera list (hidden on mobile) */}
        <aside className="hidden md:flex border-l border-zinc-900 bg-zinc-950 flex-col min-h-0">
          <SidebarBody cameras={cameras} onCameraClick={(c) => setExpandedCam(c)} />
        </aside>

        {/* Mobile drawer for sidebar contents */}
        <Sheet open={sideOpen} onOpenChange={setSideOpen}>
          <SheetContent
            side="right"
            className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100 p-0 w-80 max-w-[85vw] flex flex-col md:hidden"
            data-testid="mobile-sidebar-drawer"
          >
            <SheetTitle className="sr-only">Camera panel</SheetTitle>
            <SheetDescription className="sr-only">PTZ controls, camera list, motion alerts</SheetDescription>
            <div className="p-3 border-b border-zinc-900 mono text-[10px] uppercase tracking-widest text-cyan-400">
              Camera Panel
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <SidebarBody
                cameras={cameras}
                onCameraClick={(c) => { setSideOpen(false); setExpandedCam(c); }}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Expanded overlay */}
      {expandedCam && (
        <ExpandedView
          cam={expandedCam}
          onClose={() => setExpandedCam(null)}
        />
      )}
      <MobileBottomNav active="dashboard" />
    </div>
  );
}

/* Full-screen expanded camera view. Auto-requests browser fullscreen when the
   device rotates to landscape (so users get a real "kiosk" playback experience,
   like Hikvision iVMS-4500 / Dahua DMSS mobile apps). */
function ExpandedView({ cam, onClose }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    const enterIfLandscape = async () => {
      if (!isTouchDevice) return; // desktop keeps normal overlay
      const mq = window.matchMedia("(orientation: landscape)");
      if (mq.matches && !document.fullscreenElement) {
        try { await el.requestFullscreen({ navigationUI: "hide" }); } catch { /* fullscreen may be blocked */ }
      } else if (!mq.matches && document.fullscreenElement === el) {
        try { await document.exitFullscreen(); } catch { /* ignore */ }
      }
    };
    enterIfLandscape();
    const mq = window.matchMedia("(orientation: landscape)");
    const onOrient = () => enterIfLandscape();
    mq.addEventListener("change", onOrient);
    return () => {
      mq.removeEventListener("change", onOrient);
      if (document.fullscreenElement === el) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-testid="expanded-view"
      className="fixed inset-0 bg-black/95 z-50 flex flex-col p-2 sm:p-4"
      onClick={onClose}
    >
      <div className="flex items-center justify-between mb-2 sm:mb-3 shrink-0">
        <div className="mono text-[11px] text-cyan-400 uppercase tracking-widest truncate pr-2">
          CH{String(cam.channel).padStart(2, "0")} · {cam.name}
        </div>
        <Button
          data-testid="close-expanded"
          variant="ghost"
          onClick={onClose}
          className="rounded-none text-zinc-400 hover:text-red-400 shrink-0"
        >
          <X size={16} />
        </Button>
      </div>
      <div className="flex-1 min-h-0 max-w-[1400px] mx-auto w-full flex" onClick={(e) => e.stopPropagation()}>
        <VideoTile camera={cam} expanded />
      </div>
    </div>
  );
}

function SidebarBody({ cameras, onCameraClick }) {
  return (
    <>
      <div className="p-3 flex-1 overflow-auto">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Camera List</div>
        <ul className="space-y-1">
          {cameras.map((c) => (
            <li
              key={c.id}
              data-testid={`camera-list-${c.channel}`}
              onClick={() => onCameraClick(c)}
              className="flex items-center gap-2 border border-zinc-900 hover:border-zinc-700 px-2 py-1.5 cursor-pointer"
            >
              <Circle size={8} className={c.is_online === false ? "text-red-500 fill-red-500" : "text-emerald-500 fill-emerald-500"} />
              <div className="flex-1 min-w-0">
                <div className="mono text-[11px] text-zinc-200 truncate flex items-center gap-1.5">
                  <span>CH{String(c.channel).padStart(2, "0")} · {c.name}</span>
                  {c.type === "onvif" && (
                    <span className="mono text-[8px] uppercase tracking-widest px-1 py-px border border-amber-500/60 text-amber-400 whitespace-nowrap">ONVIF</span>
                  )}
                </div>
                {c.location && <div className="mono text-[9px] text-zinc-500 truncate">{c.location}</div>}
              </div>
              <CameraIcon size={11} className="text-zinc-600" />
            </li>
          ))}
          {cameras.length === 0 && (
            <li className="mono text-[10px] text-zinc-600 uppercase tracking-widest p-2">
              No cameras
            </li>
          )}
        </ul>
      </div>

      <div className="border-t border-zinc-900 px-3 py-2 mono text-[10px] text-zinc-600">
        <div className="flex justify-between">
          <span>ONLINE</span>
          <span className="text-emerald-400">{cameras.filter((c) => c.is_online !== false).length}</span>
        </div>
        <div className="flex justify-between">
          <span>OFFLINE</span>
          <span className="text-red-400">{cameras.filter((c) => c.is_online === false).length}</span>
        </div>
      </div>

      {/* Motion Detection Alerts */}
      <MotionAlerts />
    </>
  );
}
